<script lang="ts">
  import MetalFrame from './MetalFrame.svelte';
  import { END_GAME } from '../../game/constants/hud';
  import type { MatchInfoState } from '../../game/world';

  interface Props {
    info: MatchInfoState;
    /** The local player's team, 0-based. The winner is derived from it and `info.won`. */
    selfTeam: number;
  }

  const { info, selfTeam }: Props = $props();

  const winningTeam = $derived(info.won ? selfTeam : 1 - selfTeam);

  /**
   * Amber below the top third of the field, red below two thirds, green only when the rank falls
   * *exactly* on two thirds — so green is all but unreachable. Faithful: this is the comparison the
   * original made, and changing it changes what a player reads their placement as.
   */
  const rankColor = $derived.by(() => {
    if (info.maxPlayers < END_GAME.minimumRankedField) return END_GAME.good;
    const twoThirds = (2 / 3) * info.maxPlayers;
    if (info.rank > twoThirds) return END_GAME.bad;
    if (info.rank < twoThirds) return END_GAME.middling;
    return END_GAME.good;
  });

  interface Row {
    readonly label: string;
    readonly value: string;
    readonly color: string;
  }

  const yesNo = (value: boolean, whenTrue: string): Row['color'] =>
    value ? whenTrue : END_GAME.ink;

  const rows = $derived<readonly Row[]>([
    { label: 'Kills', value: String(info.kills), color: END_GAME.good },
    { label: 'Assists', value: String(info.assists), color: END_GAME.good },
    { label: 'Deaths', value: String(info.deaths), color: END_GAME.bad },
    {
      label: 'Swapped',
      value: info.teamSwapped ? 'Yes' : 'No',
      color: yesNo(info.teamSwapped, END_GAME.bad),
    },
    {
      label: 'Balanced',
      value: info.autoBalanced ? 'Yes' : 'No',
      color: yesNo(info.autoBalanced, END_GAME.good),
    },
    { label: 'Total Healing', value: String(info.totalHeal), color: END_GAME.good },
    { label: 'Damage To Base', value: String(info.damageToBase), color: END_GAME.good },
    {
      label: 'Won',
      value: info.won ? 'Yes' : 'No',
      color: info.won ? END_GAME.good : END_GAME.bad,
    },
  ]);

  const lastRowY = $derived(
    END_GAME.rows.firstBaselineFromTop + (rows.length - 1) * END_GAME.rows.lineHeight,
  );

  // A zero payout means a guest: the server pays no credits to a connection with no account, and
  // there is no other way to earn nothing. Printing "0" here would read as "you played badly".
  const creditsText = $derived(
    info.gainedCredits === 0
      ? END_GAME.notLoggedIn
      : `Credits Earnt: ${String(info.gainedCredits)}`,
  );
</script>

<!-- The end-of-round scoreboard: a metal panel over a dimmed screen, gone after 9.5 seconds. -->
<div class="endgame" role="status" data-testid="end-game">
  <!-- The dim is its own layer: the original drew it at 0.85 alpha and then reset to 1 for the
       panel, so fading the panel with it would be a change nobody asked for. -->
  <div
    class="endgame__dim"
    style:background-color={END_GAME.dim.color}
    style:opacity={END_GAME.dim.alpha}
  ></div>
  <div
    class="endgame__panel"
    style:width="{END_GAME.panel.width}px"
    style:height="{END_GAME.panel.height}px"
    style:color={END_GAME.ink}
  >
    <MetalFrame />

    <p
      class="endgame__heading wis-tint"
      style:top="{END_GAME.heading.baselineFromTop}px"
      style:font-size="{END_GAME.heading.font}px"
      style:color={END_GAME.teamColors[winningTeam === 0 ? 0 : 1]}
      style:--wis-tint-shadow={END_GAME.teamShadows[winningTeam === 0 ? 0 : 1]}
    >
      {END_GAME.teamLabels[winningTeam === 0 ? 0 : 1]} Team Wins
    </p>

    <p
      class="endgame__rank"
      style:top="{END_GAME.rankLine.baselineFromTop}px"
      style:font-size="{END_GAME.rankLine.font}px"
    >
      <!--
        Four spans and a flex gap rather than one string with spaces in it: only the rank number is
        coloured, and whitespace between inline elements is trimmed by the compiler and reflowed by
        the formatter — which silently renders "Rank: 3 / 9" as "Rank:3/9".
      -->
      <span>Rank:</span>
      <span class="wis-tint" style:color={rankColor}>{info.rank}</span>
      <span>/</span>
      <span>{info.maxPlayers}</span>
    </p>

    <dl class="endgame__rows" style:font-size="{END_GAME.rows.font}px">
      {#each rows as row, index (row.label)}
        <div
          class="endgame__row"
          style:top="{END_GAME.rows.firstBaselineFromTop + index * END_GAME.rows.lineHeight}px"
        >
          <dt
            class="endgame__label"
            style:right="calc(50% + {END_GAME.rows.labelGap - END_GAME.rows.hingeFromCentre}px)"
          >
            {row.label}
          </dt>
          <dd
            class="endgame__value"
            class:wis-tint={row.color !== END_GAME.ink}
            style:left="calc(50% + {END_GAME.rows.valueGap + END_GAME.rows.hingeFromCentre}px)"
            style:color={row.color}
          >
            {row.value}
          </dd>
        </div>
      {/each}
    </dl>

    <p
      class="endgame__total"
      style:top="{lastRowY + END_GAME.total.scoreGap}px"
      style:font-size="{END_GAME.total.font}px"
    >
      Score: {info.mvpScore}
    </p>
    <p
      class="endgame__total"
      style:top="{lastRowY + END_GAME.total.creditsGap}px"
      style:font-size="{END_GAME.total.creditsFont}px"
      data-testid="end-game-credits"
    >
      {creditsText}
    </p>
  </div>
</div>

<style>
  .endgame {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
  }

  .endgame__dim {
    position: absolute;
    inset: 0;
  }

  .endgame__panel {
    position: relative;
  }

  .endgame__rank {
    display: flex;
    justify-content: center;
    /* One space at this font, which is what separated the parts before they became elements. */
    gap: 0.25em;
  }

  .endgame__heading,
  .endgame__rank,
  .endgame__total {
    position: absolute;
    left: 0;
    width: 100%;
    margin: 0;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    transform: translateY(-0.78em);
  }

  .endgame__rows {
    position: absolute;
    inset: 0;
    margin: 0;
  }

  .endgame__row {
    position: absolute;
    left: 0;
    width: 100%;
  }

  .endgame__label,
  .endgame__value {
    position: absolute;
    margin: 0;
    line-height: 1;
    white-space: nowrap;
    transform: translateY(-0.78em);
  }
</style>
